/**
 * Payer-facing wording for a payment that ended without being completed.
 *
 * The API decides *which* key applies (`session.swap.failureCopyKey`, mapped
 * server-side from the intent's terminal status); this file decides only the
 * words. Nothing here re-derives status logic, and nothing here names the
 * mechanism behind the payment — the payer chose a token and sent it, and that
 * is all they were ever told.
 *
 * Two rules the copy has to keep:
 *  - no promise about refund TIMING, which is unverified;
 *  - for a short deposit, only the fact we actually know — less arrived than
 *    the amount shown — because what becomes of those funds is still open.
 */
export interface PaymentFailureCopy {
  title: string;
  body: string;
}

const COPY: Record<string, PaymentFailureCopy> = {
  'checkout.swap.failure.incomplete_deposit': {
    title: 'Payment incomplete',
    body:
      'Less arrived than the amount shown, so this payment could not be completed. ' +
      'Please contact the merchant with your transaction details before sending anything else.',
  },
  'checkout.swap.failure.refunded': {
    title: 'Payment returned',
    body:
      'This payment could not be completed, and your funds are being returned to the ' +
      'refund address you provided.',
  },
  'checkout.swap.failure.failed': {
    title: 'Payment could not be completed',
    body:
      'Something went wrong while processing your payment. Anything you sent goes back to ' +
      'the refund address you provided. Please contact the merchant with your transaction details.',
  },
  'checkout.swap.failure.expired': {
    title: 'Payment window closed',
    body:
      'No payment arrived before the deadline, so this order was closed. ' +
      'You can start again if the link is still valid.',
  },
  'checkout.swap.failure.unknown': {
    title: 'Payment could not be completed',
    body:
      'This payment could not be completed. If you sent funds, please contact the merchant ' +
      'with your transaction details.',
  },
};

/**
 * Copy for a key, or `null` when there is none to show.
 *
 * An unrecognized key falls back to the generic sentence rather than to
 * nothing: the server may add a key before this app is redeployed, and a payer
 * whose money did not arrive must never be shown an empty page.
 */
export function paymentFailureCopy(
  copyKey: string | null | undefined,
): PaymentFailureCopy | null {
  if (!copyKey) return null;
  return COPY[copyKey] ?? COPY['checkout.swap.failure.unknown'];
}
